import React, { Fragment, Dispatch, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, IconButton } from '@material-ui/core';
import Close from "@material-ui/icons/Close";

interface RejectPopupProps {
    open: boolean;
    partNo: string;
    mode: 'single' | 'all' | 'partial';
    returnQty?: number;
    onClose: () => void;
    rejectionReason?: string;
    approvedQty?: number;
    onReject: (reason: string, approveQty: number) => void
}

const RejectPopup: React.FC<RejectPopupProps> = ({ open, partNo, mode, returnQty = 0, onClose, onReject, rejectionReason: initialReason, approvedQty: initialApprovedQty }) => {

    const [rejectionReason, setRejectionReason] = useState('');


    const [approveQty, setApproveQty] = useState<string>(initialApprovedQty ? initialApprovedQty.toString() : '');


    useEffect(() => {
        if (open) {
            // Reject All has no per-item context, so never pre-fill it with a previous item's data
            if (mode === 'all') {
                setRejectionReason('');
                setApproveQty('');
                setQtyError('');
                return;
            }

            setRejectionReason(initialReason || '');
            setApproveQty(
                initialApprovedQty !== undefined && initialApprovedQty !== null
                    ? initialApprovedQty.toString()
                    : ''
            );
        }
    }, [open, mode, initialReason, initialApprovedQty]);


    const [qtyError, setQtyError] = useState<string>('');

    const handleQtyChange = (value: string) => {
        const qty = parseInt(value);

        if (isNaN(qty)) {
            setQtyError('');
            setApproveQty('');
            return;
        }

        if (qty < 1) {
            setQtyError('Minimum quantity is 1');
            setApproveQty(value);
        } else if (qty >= returnQty) {
            setQtyError(`Quantity must be less than ${returnQty}`);
            setApproveQty(value);
        } else {
            setQtyError('');
            setApproveQty(value);
        }
    };


    const handleReject = () => {

        if (mode === 'partial') {
            const qty = parseInt(approveQty);
            if (!isNaN(qty) && qty > 0 && qty < returnQty && rejectionReason.trim()) {
                onReject(rejectionReason, qty);
                setApproveQty('');
                setRejectionReason('');
                setQtyError('');
            }
        }

        else {
            if (rejectionReason.trim()) {
                onReject(rejectionReason, 0);
                setRejectionReason('');
            }
        }
    };

    const handleClose = () => {
        setRejectionReason('');
        setApproveQty('');
        setQtyError('');
        onClose();
    };

    const isDisabled = mode === 'partial'
        ? !approveQty || !!qtyError || !rejectionReason.trim()
        : !rejectionReason.trim();


    return (

        <Dialog open={open} onClose={() => { }} maxWidth="sm" fullWidth disableEscapeKeyDown>

            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <div>
                        {mode === "single" && (
                            <div>
                                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                                    Reject (Part No: {partNo})
                                </div>
                            </div>
                        )}
                        {mode === "all" && (<div style={{ fontSize: "20px", fontWeight: "bold" }}>
                            Reason of Rejection
                        </div>)}
                        {mode === "partial" && (
                            <div>
                                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                                    Partially Approve (Part No: {partNo})
                                </div>
                            </div>
                        )}
                    </div>
                    <IconButton onClick={handleClose}>
                        <Close style={{ color: "#000" }} />
                    </IconButton>
                </Grid>
            </DialogTitle>

            {mode !== "partial" && (<hr style={{ margin: "0" }} />)}


            <DialogContent className="wempopup" style={{ paddingTop: "24px", paddingBottom: "0px" }}>

                {mode === "partial" && (
                    <>

                        <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
                            <div style={{ flex: 0.7, fontSize: "13px", fontWeight: 600, color: "#133F8A", textAlign: "center" }}>Return Qty</div>
                            <div style={{ flex: 1, fontSize: "13px", fontWeight: 600, color: "#133F8A", textAlign: "center" }}>Approve Return Qty</div>
                            <div style={{ flex: 2, fontSize: "13px", fontWeight: 600, color: "#133F8A", textAlign: "center" }}>Reason  <span style={{ color: "#d32f2f" }}>*</span> </div>
                        </div>


                        <div style={{
                            margin: "16px -24px",
                            borderTop: "2px solid #e0e0e0",
                            height: "1px"
                        }} />

                        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                            <TextField
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={returnQty}
                                disabled
                                size="small"
                                style={{ flex: 0.7 }}
                                className="commonRoundedInputs"
                            />
                            <TextField
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={approveQty}
                                onChange={(e) => handleQtyChange(e.target.value)}
                                placeholder="Enter qty"
                                error={!!qtyError}
                                size="small"
                                style={{ flex: 0.9 }}
                                className="commonRoundedInputs"
                            />

                            <TextField
                                type="text"
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                value={rejectionReason}
                                onChange={(e) => {

                                    const cleanedValue = e.target.value
                                        .replace(/[\n\r]/g, '')
                                        .slice(0, 100);
                                    setRejectionReason(cleanedValue);
                                }}
                                placeholder="Reason"
                                size="small"
                                style={{ flex: 2 }}
                                InputProps={{
                                    style: {
                                        borderRadius: '12px'
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        padding: "12px 16px",
                                        textAlign: "left"
                                    }
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>

                            <div style={{ flex: 0.7 }}></div>

                            <div style={{
                                flex: 0.9,
                                fontSize: "11px",
                                color: qtyError ? "#d32f2f" : "#999",
                                textAlign: "start",
                                marginTop: '-30px',
                                whiteSpace: 'nowrap'
                            }}>
                                {qtyError || `Must be between 1 and ${returnQty - 1}`}
                            </div>

                            <div style={{
                                flex: 2,
                                fontSize: "11px",
                                color: "#999",
                                textAlign: "center"
                            }}>
                                {rejectionReason.length}/100 characters
                            </div>
                        </div>

                        <div style={{
                            margin: "16px -24px",
                            borderTop: "2px solid #e0e0e0",
                            height: "1px"
                        }} />
                    </>
                )}

                {mode !== "partial" && (
                    <TextField
                        autoFocus
                        label="Reason"
                        type="text"
                        fullWidth
                        multiline
                        rows={6}
                        className="rounded-input-dealer"
                        variant="outlined"
                        value={rejectionReason}
                        onChange={(e) => {

                            const cleanedValue = e.target.value
                                .replace(/[\n\r]/g, '')
                                .slice(0, 100);
                            setRejectionReason(cleanedValue);
                        }}
                        placeholder="Provide reason for rejection"
                        helperText={<span style={{ color: '#999' }}>{rejectionReason.length}/100 characters</span>}
                        style={{ marginTop: "16px" }}
                    />
                )}
            </DialogContent>

            {mode !== "partial" && (<hr style={{ margin: "0", borderTop: "1px solid #e0e0e0" }} />)}

            <DialogActions style={{
                display: "flex",
                justifyContent: "end",
                padding: "16px",
                gap: "12px",
            }}>
                <Button
                    onClick={handleReject}
                    variant="contained"
                    color="primary"
                    disabled={isDisabled}
                    style={{ borderRadius: "25px", padding: "8px 24px" }}
                >
                    Proceed
                </Button>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    style={{ borderRadius: "25px", padding: "8px 24px" }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default RejectPopup;
